import { Grid, Typography } from '@mui/material';
import { AddItemButton, ToolbarButton } from '@tassat/button';
import { ActionTypes, AdminRoleId } from '@tassat/common-definitions';
import { WalletForm } from '@tassat/forms';
import { Export as ExportIcon } from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { checkPendingTransactions, submitWalletForm } from 'Containers/admin/forms/helpers/walletFormFn';
import { exportFirmWalletData } from 'Lib/exports/firmExports';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountsForFirm, getAllWalletsByFirmId } from 'Selectors/accounts';
import { getAcgSelectOptions } from 'Selectors/acg';
import { getAccountRelationships, getUserAdminRoleId } from 'Selectors/common';

import FirmWalletDetails from './firmWalletDetails';
import FirmWalletsAgGrid from './firmWalletsAgGrid';

import { IFirmWalletsProps } from '.';

const EXCLUDE_LOCKED_OPTS = { includeLocked: false };

export function FirmWallets(props: IFirmWalletsProps) {
    const {
        classes,
        firm,
        assetPair
    } = props;

    const userRole = useAppSelector(getUserAdminRoleId);
    const acgSelectOptions = useAppSelector((state) => getAcgSelectOptions(state, false, false));
    const accountRelationships = useAppSelector(getAccountRelationships);

    const accounts = useAppSelector((state) => getAccountsForFirm(state, firm?.FirmId, EXCLUDE_LOCKED_OPTS));
    const wallets = useAppSelector((state) => getAllWalletsByFirmId(state, firm?.FirmId));

    const [ showClosedWallets, setShowClosedWallets ] = React.useState<boolean>(false);
    const [ walletFormOpen, setWalletFormOpen ] = React.useState<boolean>(false);

    return (
        <>
            <Switch>
                <Route
                    exact
                    path='/admin/firms/:firmId/accounts/:accountId'
                    render={props => (
                        <FirmWalletDetails
                            {...props}
                            assetPair={assetPair}
                        />
                    )}
                />
                <Route
                    path='/admin/firms/:firmId'
                    render={() => (
                        <Grid
                            className={classes.root}
                            container
                            direction='column'
                            justifyContent='flex-start'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Grid
                                alignItems='center'
                                className={classes.buttonToolbar}
                                container
                                direction='row'
                                item
                                justifyContent='flex-start'
                                spacing={0}
                                xs={12}
                            >
                                {(userRole === AdminRoleId.BANK_OPS || userRole === AdminRoleId.CLIENT_SERVICES) &&
                                    !firm?.IsClosed && (
                                    <AddItemButton
                                        id='firmWallets_AddWalletButton'
                                        onClick={() => setWalletFormOpen(true)}
                                        text='New Account'
                                    />
                                )}
                                <Typography
                                    className={`${classes.closedWalletsButton} ${showClosedWallets ? classes.closedWalletsFilterActive : ''}`}
                                    onClick={() => setShowClosedWallets(!showClosedWallets)}
                                    variant='body1'
                                >
                                    {!showClosedWallets ? 'Show' : 'Hide'} Closed Accounts
                                </Typography>
                            </Grid>
                            <Grid
                                className={classes.walletsGridContainer}
                                item
                                xs={12}
                            >
                                <ToolbarButton
                                    className={classes.exportButton}
                                    Icon={ExportIcon}
                                    iconClassName={classes.exportIcon}
                                    onClick={() => exportFirmWalletData(firm?.FirmId, showClosedWallets)}
                                    text='Export Accounts'
                                />
                                <FirmWalletsAgGrid
                                    firmId={firm.FirmId}
                                    showClosedWallets={showClosedWallets}
                                />
                                <FormModal
                                    Form={WalletForm}
                                    formProps={{
                                        acgSelectOptions,
                                        accountSelectOptions: accounts.map((account) => ({
                                            label: account.Account,
                                            value: account.AccountId,
                                            refData: account
                                        })),
                                        actionType: ActionTypes.add,
                                        firmId: firm?.FirmId,
                                        firm,
                                        firmWallets: wallets,
                                        editAccount: undefined,
                                        checkPendingTransactions,
                                        submitWalletForm,
                                        accountRelationships: accountRelationships
                                    }}
                                    modalWidth='744px'
                                    onClose={() => setWalletFormOpen(false)}
                                    open={walletFormOpen}
                                    title='Add New Account'
                                />
                            </Grid>
                        </Grid>
                    )}
                />
            </Switch>
        </>
    );
}